import { isCourseFree, issuesCredential } from "@repo/shared";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useAvailableCourses } from "~/api/queries/useAvailableCourses";
import { courseQueryOptions } from "~/api/queries/useCourse";
import { credentialLabelShort } from "~/types/certificate";
import { resolveDirectEnrollHref } from "~/utils/courseLinks";

import { leadSentences } from "../../lib/excerpt";
import { CheckMark } from "../CuadernoVivo/CheckMark";

import type { SupportedLanguages } from "@repo/shared";

/**
 * "El protagonista" — the admin-configurable featured FREE course as a stage card:
 * GRATIS flag, plain-language info + certificate seal on the left, and a phone frame
 * playing the course's 15s vertical video (muted/loop/playsinline) on the right.
 * Featured = courses.isFeatured (exclusive, set from admin); falls back to the first
 * free course while none is flagged.
 *
 * DESKTOP: intacto — dos columnas (contenido | teléfono), botones en línea y el
 * "sin tarjeta…" debajo de ellos.
 * MOBILE (solo `max-md:`): el video va PRIMERO, los botones ocupan todo el ancho y el
 * "sin tarjeta…" queda pegado al botón de inscripción. Se logra con `order`, sin tocar
 * una sola clase de desktop.
 */
const PROMO_VIDEO_SRC = "/promo/promo-academia-15s.mp4";
// Cuadro real del propio video (540×960), extraído con ffmpeg. Ver el comentario del
// <video> más abajo: el thumbnail del curso NO sirve acá porque es apaisado.
const PROMO_POSTER_SRC = "/promo/promo-academia-15s.poster.jpg";

export function FeaturedFreeCourse() {
  const { t, i18n } = useTranslation();
  const language = i18n.language as SupportedLanguages;
  const { data: courses, isLoading } = useAvailableCourses({ language });
  const videoRef = useRef<HTMLVideoElement>(null);

  const featured = courses?.find((c) => c.isFeatured) ?? courses?.find(isCourseFree);

  // El catálogo NO publica `credentialType`, así que la credencial se resuelve con el
  // detalle del curso destacado —la misma consulta que después cachea su ficha—. Sin esto
  // la tarjeta más visible del sitio prometía "certificado verificable" siempre, incluso
  // en un curso con la credencial apagada.
  const { data: featuredDetail } = useQuery({
    ...courseQueryOptions(featured?.slug ?? "", language),
    enabled: Boolean(featured?.slug),
  });
  const credentialType = featuredDetail?.credentialType;

  // Autoplay REAL. El atributo `autoplay` solo no alcanza: este <video> lo crea React
  // recién cuando llegan los datos del curso, y para entonces la sección está abajo del
  // fold. Chrome declina el arranque y NO lo reintenta nunca, así que el video quedaba
  // cargado del todo (readyState 4) y pausado en el frame 0 — leído como "congelado".
  // Lo arrancamos a mano, igual que HeroIllustration en LaunchHero.tsx, y lo atamos a un
  // IntersectionObserver para no reproducir fuera de pantalla (batería y datos).
  // La promesa de play() se traga a propósito: si una política del navegador la rechaza,
  // queda el primer cuadro del video, que es exactamente el fallback que corresponde.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Sin movimiento si el usuario lo pidió: se queda el primer cuadro del video.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      video.removeAttribute("autoplay");
      video.pause();
      return;
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) void video.play().catch(() => {});
        else video.pause();
      },
      { threshold: 0.25 },
    );
    io.observe(video);
    return () => io.disconnect();
  }, [featured?.id]);

  // Skeleton con el id definitivo desde el PRIMER render: los links "/#cursos" desde
  // otras páginas aterrizan acá al instante (useHashScroll), sin flash del hero.
  if (isLoading) {
    return (
      <section
        id="cursos"
        // El chip GRATIS sobresale -14px sobre la card: sin este extra, el header sticky
        // le tapa la punta al aterrizar desde el menú.
        className="relative scroll-mt-[118px] px-5 pb-24 pt-2 max-md:pb-10 sm:px-8"
      >
        <div className="mx-auto min-h-[420px] max-w-[1000px] animate-pulse rounded-[20px] border-[1.5px] border-landing-border bg-landing-paper" />
      </section>
    );
  }

  if (!featured) return null;

  const gets = t("landing.launch.free.gets", { returnObjects: true }) as string[];
  // Contrato estable con la app: la misma url sirve al anónimo (se registra) y al que ya
  // tiene sesión (le pide confirmación). Por eso la home puede escribirla sin saber si hay
  // sesión, que es lo que la mantiene servible desde el servidor.
  const enrollHref = resolveDirectEnrollHref({ slug: featured.slug });

  return (
    <section
      id="cursos"
      // El chip GRATIS sobresale -14px sobre la card: sin este extra, el header sticky
      // le tapa la punta al aterrizar desde el menú.
      className="relative scroll-mt-[118px] px-5 pb-24 pt-2 max-md:pb-10 sm:px-8"
    >
      <div className="relative mx-auto grid max-w-[1000px] items-center gap-8 rounded-[20px] border-[1.5px] border-landing-border bg-landing-paper p-8 shadow-landing-xl sm:p-12 md:grid-cols-[1.15fr_0.85fr] md:gap-11">
        <span className="absolute -top-3.5 left-8 rotate-[-2deg] rounded-[8px] bg-landing-red px-4 py-2 text-[12px] font-extrabold uppercase tracking-[0.14em] text-white shadow-landing-red sm:left-11">
          {t("landing.launch.free.flag")}
        </span>
        <div>
          <h2 className="font-display text-[clamp(26px,3.4vw,38px)] font-extrabold leading-[1.1] tracking-tight text-landing-text">
            {featured.title}
          </h2>
          {featured.description ? (
            <p className="mt-2 max-w-[48ch] text-[15.5px] text-landing-muted">
              {leadSentences(featured.description, 150)}
            </p>
          ) : null}
          {/* CheckMark, no un ✓ suelto: es el único check de la superficie pública
              (ver CuadernoVivo/CheckMark.tsx). Acá había quedado un glifo pelado, más
              chico que el disco verde del resto de la home. */}
          <ul className="mt-4 flex flex-col gap-2 text-[15px] text-landing-text">
            {gets.map((item) => (
              <li key={item} className="flex gap-2.5">
                <CheckMark className="mt-[4px]" />
                <span className="min-w-0">{item}</span>
              </li>
            ))}
          </ul>
          <p className="landing-meta mt-4 uppercase tracking-[0.1em] text-landing-muted">
            {t("landing.courses.card.chapters", { count: featured.courseChapterCount })} ·{" "}
            {t("landing.launch.free.metaTail")}
          </p>

          {/* Credencial: sello lacrado con ribbon, en un solo SVG (los divs apilados con
              clip-path se rompían). Ocupa el lugar del chip anterior.
              Sólo se pinta si este curso entrega algo, y dice qué entrega: un curso gratis
              da constancia, no certificado. */}
          {issuesCredential(credentialType) && (
            <div className="mt-4 flex items-center gap-3 max-md:justify-center">
              <svg
                width="52"
                height="62"
                viewBox="0 0 52 62"
                aria-hidden="true"
                className="shrink-0"
              >
                <path d="M16 38 L16 60 L22.5 53.5 L29 60 L29 38 Z" fill="#22646d" />
                <path d="M23 38 L23 60 L29.5 53.5 L36 60 L36 38 Z" fill="#2B7D87" />
                <circle
                  cx="26"
                  cy="24"
                  r="22.5"
                  fill="none"
                  stroke="#2B7D87"
                  strokeOpacity="0.45"
                  strokeWidth="2"
                  strokeDasharray="4 4"
                />
                <circle cx="26" cy="24" r="17.5" fill="#2B7D87" />
                <path
                  d="M18.5 24.5 l5 5 l10 -10.5"
                  fill="none"
                  stroke="#FFFFFF"
                  strokeWidth="3.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <p className="text-[13.5px] font-bold leading-snug text-landing-teal">
                Con {credentialLabelShort(credentialType)} verificable al terminar
              </p>
            </div>
          )}

          {/* Acciones. Desktop: fila con los dos botones y el "sin tarjeta" debajo
              (w-full lo manda a su propio renglón). Mobile: columna, botones a todo el
              ancho y el "sin tarjeta" pegado al de inscripción, por `order`. */}
          <div className="mt-5 flex flex-wrap items-center gap-3 max-md:flex-col max-md:items-stretch">
            <a
              href={enrollHref}
              data-cuelume-press
              data-cuelume-release
              className="order-1 inline-flex items-center gap-2 rounded-[10px] bg-landing-red px-7 py-4 text-[15.5px] font-bold text-white shadow-landing-red transition-all duration-150 hover:-translate-y-0.5 hover:bg-landing-red-hover hover:shadow-landing-red-hover max-md:w-full max-md:justify-center"
            >
              {t("landing.launch.free.cta")} →
            </a>
            <p className="order-2 text-[13px] text-landing-muted md:order-3 md:mt-2.5 md:w-full max-md:text-center">
              {t("landing.launch.free.subCta")}
            </p>
            <a
              href={`/courses/${featured.slug}`}
              data-cuelume-hover="tick"
              data-cuelume-press
              className="order-3 inline-flex items-center gap-2 rounded-[10px] border-[1.5px] border-landing-teal/50 bg-landing-paper/70 px-5 py-[15px] text-[14px] font-bold text-landing-teal transition-colors hover:border-landing-teal hover:bg-landing-teal-light md:order-2 max-md:w-full max-md:justify-center"
            >
              {t("landing.launch.free.verTemario")}
            </a>
          </div>
        </div>

        {/* El teléfono. En mobile va PRIMERO (order), en desktop queda a la derecha. */}
        <div className="flex flex-col items-center gap-3 max-md:order-first">
          <div className="relative w-[min(230px,60vw)] overflow-hidden rounded-[28px] border-[6px] border-[#2c2622] bg-[#14100e] shadow-landing-xl [aspect-ratio:9/17]">
            <span
              aria-hidden="true"
              className="absolute left-1/2 top-2.5 z-10 h-[5px] w-[58px] -translate-x-1/2 rounded-full bg-[#3b332d]"
            />
            <video
              ref={videoRef}
              className="h-full w-full object-cover"
              src={PROMO_VIDEO_SRC}
              // El poster es un CUADRO DEL PROPIO VIDEO, no el thumbnail del curso. El
              // thumbnail es APAISADO y este marco es 9/17: con `object-cover` se
              // recortaba al 29% central y se leía el título partido al medio de las
              // palabras ("ción a la / cia Artifi"). Y no era sólo un flash de carga —
              // el poster es lo que queda FIJO con `prefers-reduced-motion` (lo pausamos
              // abajo a propósito) y cuando el navegador deniega el autoplay.
              poster={PROMO_POSTER_SRC}
              autoPlay
              muted
              loop
              playsInline
              // "auto", como los clips del hero que sí arrancan: con "metadata" el
              // elemento llega al momento del autoplay sin datos suficientes.
              preload="auto"
              aria-label={featured.title}
            />
          </div>
          <span className="landing-hand rotate-[-2deg] text-[16px] text-landing-teal">
            {t("landing.launch.free.phoneNote")} ↑
          </span>
        </div>
      </div>
    </section>
  );
}
